/* Copyright (c) 2021 Caring Transitions Online Auctions. All Rights Reserved. */

// library imports
import { Component, createRef } from 'react';
import { Button, Form, Row, Col, OverlayTrigger, Tooltip, Table } from 'react-bootstrap';
import axios from 'axios';
import DatePicker from "react-datepicker";
import moment from 'moment';
import Excel from 'assets/CSV.svg'
// project imports
import { getSellerUrl, getSellerHeaders } from 'shared/http';
import { locationlist, revenueConsolidateReport, membershipConsolidateReport, salesConsolidateReport, userConsolidateReport, userSummaryReport, membershipSummaryReport, salesSummaryReport, revenueSummaryReport } from 'shared/settings/apiconfig';
import { ArrayValueReorder } from 'shared/modifiers';
import Chart from 'react-apexcharts';

// style imports
import "react-datepicker/dist/react-datepicker.css";
import './Admin.scss';
import './Leadership.scss';
import { NoData } from 'features/components/redirect/NoData';
/**
 * this page is used to show leadership report in table
 * 
 * @param props
 * 
 * @return leadership report html code
 * 
**/

const salesBarChartData = (series) => {
    const categories = series.map(item => {
        let startDate = moment(item.startDate).format('DD-MM-YYYY');
        if (item.range.includes("year")) {
            return moment(startDate, "DD-MM-YYYY").year();
        } else if (item.range.includes("month")) {
            return moment(startDate, "DD-MM-YYYY").format("MMM");
        } else if (item.range.includes("week")) {
            return startDate;
        } else if (item.range.includes("day")) {
            return startDate;
        }
    })

    let seriesData = [
        {
            name: "Total Sales Closed",
            data: series.map(item => parseFloat(item.totalSalesClosed) || 0)
        },
        {
            name: "Completed Online Sales Count",
            data: series.map(item => parseFloat(item.completedOnlineSale) || 0)
        },
        {
            name: "Average Sale Length",
            data: series.map(item => parseFloat(item.averageSaleLength) || 0)
        },
        {
            name: "Total Items Sold",
            data: series.map(item => parseFloat(item.totalItemsSold) || 0)
        },
        {
            name: "Total Items Not Sold",
            data: series.map(item => parseFloat(item.totalItemsNotSold) || 0)
        },
        {
            name: "Total Items Listed",
            data: series.map(item => parseFloat(item.itemListed) || 0)
        },
        {
            name: "Percentage of Items Sold",
            data: series.map(item => parseFloat(item.itemSoldPercentage) || 0)
        },
        {
            name: "Location Actively using CTBids",
            data: series.map(item => parseFloat(item.activeLocations) || 0)
        },
        {
            name: "Owners Selling on CTBids",
            data: series.map(item => parseFloat(item.ownerSelling) || 0)
        },   
    ];

    seriesData = seriesData.filter(series => series.data.some(elem => elem > 0));

    return {
        series: seriesData,
        options: {
            chart: {
                type: 'bar',
                height: 350,
                toolbar: {
                    show: true,
                    tools: {
                        download: true,
                    },
                    export: {
                        csv: {
                            filename: "sales-report",
                            columnDelimiter: ',',
                            headerCategory: 'Range',
                            headerValue: 'value',
                            formatter: function(val) {
                                return val.toFixed(2);  
                            }
                        },
                        svg: {
                            filename: "sales-report",
                        },
                        png: {
                            filename: "sales-report",
                        }
                    },
                }
            },
            noData: {
                text: 'No Data Found',
            },
            colors: ['#1c9e27', '#1914EB', '#FFA500', '#1ce860', '#D32D41', '#0091D5', '#B3C100', '#6AB187', '#20283E'],
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '50%',
                    borderRadius: 15,
                    borderRadiusApplication: 'end',
                },
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                show: true,
                width: 2,
                colors: ['transparent']
            },
            xaxis: {
                categories: categories,
            },
            yaxis: {
                labels: {
                    formatter: (value) => value,
                },
            },
            fill: {
                opacity: 1
            },
            tooltip: {
                enabled: true,
                custom: function ({ seriesIndex, series, dataPointIndex, w }) {
                    const value = series[seriesIndex][dataPointIndex];
                    const seriesName = w.config.series[seriesIndex].name; 
                    return `<div style="padding: 10px; background-color: #fff; border-radius: 5px; border: 1px solid #ddd;">
                                ${seriesName} : <strong>${value}</strong>
                            </div>`
                }
            }
        }
    };
}

const userBarChartData = (data) => {

    const keys = data.map(item => {
        let startDate = moment(item.startDate).format('DD-MM-YYYY');
        if (item.range.includes("year")) {
            return moment(startDate, "DD-MM-YYYY").year();
        } else if (item.range.includes("month")) {
            return moment(startDate, "DD-MM-YYYY").format("MMM");
        } else if (item.range.includes("week")) {
            return startDate;
        } else if (item.range.includes("day")) {
            return startDate;
        }
    })

    let seriesData = [
        {
            name: "Total Users",
            data: data.map(item => parseFloat(item.totalUsers) || 0)
        },
        {
            name: "Total Registered Users",
            data: data.map(item => parseFloat(item.registeredUsers) || 0)
        },
        {
            name: "Profile Completed",
            data: data.map(item => parseFloat(item.profileCompleted) || 0)
        },
        {
            name: "Account Completed",
            data: data.map(item => parseFloat(item.accountCreated) || 0)
        },
        {
            name: "Suspended Users",
            data: data.map(item => parseFloat(item.suspendedUsers) || 0)
        },
        {
            name: "Number of Repeat Purchasers",
            data: data.map(item => parseFloat(item.repeatedPurchaser) || 0)
        },
        {
            name: "Number of New Purchasers",
            data: data.map(item => parseFloat(item.newPurchaser) || 0)
        },
        {
            name: "Number of Active Users",
            data: data.map(item => parseFloat(item.activeUsers) || 0)
        },
        {
            name: "Average Number of Bids per Item",
            data: data.map(item => parseFloat(item.averageNumberOfBids) || 0)
        }
    ];

    seriesData = seriesData.filter(series => series.data.some(elem => elem > 0));

    return {
        series: seriesData,
        options: {
            chart: {
                type: 'bar',
                height: 350,
                toolbar: {
                    show: true,
                    tools: {
                        download: true,
                    },
                    export: {
                        csv: {
                            filename: "user-report",
                            columnDelimiter: ',',
                            headerCategory: 'Range',
                            headerValue: 'value',
                            formatter: function(val) {
                                return val.toFixed(2);  
                            }
                        },
                        svg: {
                            filename: "user-report",
                        },
                        png: {
                            filename: "user-report",
                        }
                    },
                }
            },
            noData: {
                text: 'No Data Found',
            },
            colors: ['#1c9e27', '#1914EB', '#FFA500', '#1ce860', '#F4110B', '#0091D5', '#B3C100', '#6AB187', '#20283E'],
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '50%',
                    borderRadius: 15,
                    borderRadiusApplication: 'end',
                },
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                show: true,
                width: 2,
                colors: ['transparent']
            },
            xaxis: {
                categories: keys,
            },
            yaxis: {
                labels: {
                    formatter: (value) => value,
                },
            },
            fill: {
                opacity: 1
            },
            tooltip: {
                enabled: true,
                custom: function ({ seriesIndex, series, dataPointIndex, w }) {
                    const value = series[seriesIndex][dataPointIndex];
                    const seriesName =  w.config.series[seriesIndex].name;
                    return `<div style="padding: 10px; background-color: #fff; border-radius: 5px; border: 1px solid #ddd;">
                                ${seriesName} : <strong>${value}</strong>
                            </div>`
                }
            }
        }
    };
}

const membershipBarChartData = (data) => {

    const keys = data.map(item => {
        let startDate = moment(item.startDate).format('DD-MM-YYYY');
        if (item.range.includes("year")) {
            return moment(startDate, "DD-MM-YYYY").year();
        } else if (item.range.includes("month")) {
            return moment(startDate, "DD-MM-YYYY").format("MMM");
        } else if (item.range.includes("week")) {
            return startDate;
        } else if (item.range.includes("day")) {
            return startDate;
        }
    })

    let seriesData = [
        {
            name: "Membership Count",
            data: data.map(item => parseFloat(item.membershipCount) || 0)
        },
        {
            name: "Subscriber Count",
            data: data.map(item => parseFloat(item.subscriberCount) || 0)
        },
        {
            name: "New Subscriber Count",
            data: data.map(item => parseFloat(item.newSubscriberCount) || 0)
        },
        {
            name: "Expiry Count",
            data: data.map(item => parseFloat(item.expiryCount) || 0)
        },
        {
            name: "Expired Count",
            data: data.map(item => parseFloat(item.expiredCount) || 0)
        },
        {
            name: "Subscriber Revenue",
            data: data.map(item => parseFloat(item.subscriberRevenue) || 0)
        }
    ];

    seriesData = seriesData.filter(series => series.data.some(elem => elem > 0));

    return {
        series: seriesData,
        options: {
            chart: {
                height: 350,
                type: 'bar',
                toolbar: {
                    show: true,
                    tools: {
                        download: true,
                    },
                    export: {
                        csv: {
                            filename: "membership-report",
                            columnDelimiter: ',',
                            headerCategory: 'Range',
                            headerValue: 'value',
                            formatter: function(val) {
                                return val.toFixed(2);  
                            }
                        },
                        svg: {
                            filename: "membership-report",
                        },
                        png: {
                            filename: "membership-report",
                        }
                    },
                }
            },
            noData: {
                text: 'No Data Found'
            },
            colors: ['#1c9e27', '#1914EB', '#B3C100', '#EA6A47', '#0091D5'],
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '50%',
                    borderRadius: 15,
                    borderRadiusApplication: 'end',
                },
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                show: true,
                width: 2,
                colors: ['transparent']
            },
            xaxis: {
                categories: keys,
            },
            yaxis: {
                labels: {
                    formatter: (value) => value,
                },
            },
            fill: {
                opacity: 1
            },
            tooltip: {
                enabled: true,
                custom: function ({ seriesIndex, series, dataPointIndex, w }) {
                    const value = series[seriesIndex][dataPointIndex];
                    const seriesName = w.config.series[seriesIndex].name; 
                    return `<div style="padding: 10px; background-color: #fff; border-radius: 5px; border: 1px solid #ddd;">
                                ${seriesName == "Subscriber Revenue" ? `${seriesName} : <strong>$ ${value}</strong>` : `${seriesName} : <strong>${value}</strong>`}
                            </div>`
                }
            }
        },
    }
}

const salesRevenueBarChartData = (series) => {
    const categories = series.map(item => {
        let startDate = moment(item.startDate).format('DD-MM-YYYY');
        if (item.range.includes("year")) {
            return moment(startDate, "DD-MM-YYYY").year();
        } else if (item.range.includes("month")) {
            return moment(startDate, "DD-MM-YYYY").format("MMM");
        } else if (item.range.includes("week")) {
            return startDate;
        } else if (item.range.includes("day")) {
            return startDate;
        }
    })

    let seriesData = [
        {
            name: "CTBids Site Revenue",
            data: series.map(item => parseFloat(item.ctbidsRevenue) || 0)
        },
        {
            name: "Gross Sale",
            data: series.map(item => parseFloat(item.grossSale) || 0)
        },
        {
            name: "Average Sale Price",
            data: series.map(item => parseFloat(item.averageSalePrice) || 0)
        },
        {
            name: "Average Item Price",
            data: series.map(item => parseFloat(item.averageItemPrice) || 0)
        },
        {
            name: "Total Buyer's Premium",
            data: series.map(item => parseFloat(item.totalBuyersPremium) || 0)
        },
        {
            name: "Total Item Tax",
            data: series.map(item => parseFloat(item.totalItemTax) || 0)
        },
        {
            name: "Total Item Tax + Total BP Tax",
            data: series.map(item => parseFloat(item.totalTax) || 0)
        },
        // {
        //     name: "Total Sale Price",
        //     data: series.map(item => (item.totalSalePrice == "" || item.totalSalePrice == null || item.totalSalePrice == undefined) ? 0 : item.totalSalePrice)
        // },
        // {
        //     name: "Total Sale Price + Buyer's Premium",
        //     data: series.map(item => (item.totalAmount == "" || item.totalAmount == null || item.totalAmount == undefined) ? 0 : item.totalAmount)
        // },
        {
            name: "Average Listing Price",
            data: series.map(item => parseFloat(item.averageListingPrice) || 0)
        },
    ];

    seriesData = seriesData.filter(series => series.data.some(elem => elem > 0));

    return {
        series: seriesData,
        options: {
            chart: {
                type: 'bar',
                height: 350,
                toolbar: {
                    show: true,
                    tools: {
                        download: true,
                    },
                    export: {
                        csv: {
                            filename: "sales-revenue-report",
                            columnDelimiter: ',',
                            headerCategory: 'Range',
                            headerValue: 'value',
                            formatter: function(val) {
                                return val.toFixed(2);  
                            }
                        },
                        svg: {
                            filename: "sales-revenue-report",
                        },
                        png: {
                            filename: "sales-revenue-report",
                        }
                    },
                }
            },
            noData: {
                text: 'No Data Found',
            },
            colors: ['#1c9e27', '#1914EB', '#FFA500', '#1ce860', '#AC3E31', '#0091D5', '#B3C100', '#6AB187'],
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '50%',
                    borderRadius: 15,
                    borderRadiusApplication: 'end',
                },
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                show: true,
                width: 2,
                colors: ['transparent']
            },
            xaxis: {
                categories: categories,
            },
            yaxis: {
                labels: {
                    formatter: (value) => value.toFixed(2),
                },
            },
            fill: {
                opacity: 1
            },
            tooltip: {
                enabled: true,
                custom: function ({ seriesIndex, series, dataPointIndex, w }) {
                    const value = series[seriesIndex][dataPointIndex];
                    const seriesName = w.config.series[seriesIndex].name; 
                    return `<div style="padding: 10px; background-color: #fff; border-radius: 5px; border: 1px solid #ddd;">
                                ${seriesName} : <strong>$ ${value.toFixed(2)}</strong>
                            </div>`
                }
            }
        }
    };
}
export default class Leadership extends Component {
    initialSearchState;
    search;
    config;
    id;
    filterSearch;

    constructor(props) {
        super(props);

        this.dataTable = createRef();
        this.errorMsg = 'Please select a date filter or range within any 12 month period if "All" is selected from the "location" dropdown'
        /* initial search state */
        this.initialSearchState = {
            starttime: { field: 'fromdate', value: '', op: '>=', join: 'AND' },
            endtime: { field: 'todate', value: '', op: '<=', join: 'AND' },
            locationid: { field: 'locationid', value: '', op: '=', join: 'AND' }
        }
        this.state = {
            search: this.initialSearchState,
            locationcheck: false,
            searchData: false,
            enableSearchData: false,
            modal: false,
            tableData: [],
            salesDetails: {},
            category: [],
            issearch: false,
            saleid: { value: '', valid: false, error: '' },
            locations: [],
            isdisabled: false,
            location: '',
            locationlength: 0,
            isActiveCheckbox: "",
            isDateFromCheckBox: "",
            checkedStartDateTime: '',
            checkedEndDateTime: '',
            SearchButton: false,
            locationAllSelected: false,
            csvColumn: [],
            errorMsg: false,
            user: {},
            membership: {},
            revenue: {},
            sales: {},
            salesChartData: {},
            userChartData: {},
            membershipChartData: {},
            salesRevenueChartData: {},
            chartView: false,
            tableView: true,
            monthDropdownValue: '',
            startDate: '',
            endDate: '',
            checkedItems: [],
            showFilter: false,
            userConst: [
                { id: "totalUsers", label: "Total Users", checked: false, tooltip: 'Total Users : All registered users to date' },
                { id: "registeredUsers", label: "Total Registered Users", checked: false, tooltip: 'Total New Registrations : The number of new registrations for the period selected' },
                { id: "profileCompleted", label: "Profile Completed", checked: false, tooltip: 'Profile Completed : All registered users Profile Completed'},
                { id: "accountCreated", label: "Account Created", checked: false, tooltip: 'Account Created : All registered users Profile Completed and Payment Completed' },
                { id: "suspendedUsers", label: "Suspended Users", checked: false, tooltip: 'Suspended Users : User who have been suspended during this time' },
                { id: "repeatedPurchaser", label: "Number of Repeat Purchasers", checked: false, tooltip: 'Number of Repeat Purchasers : Number of repeat purchases for the date range' },
                { id: "newPurchaser", label: "Number of New Purchasers", checked: false, tooltip: 'Number of New Purchasers : Number of New purchasers for the date the range' },
                { id: "activeUsers", label: "Number of Active Users", checked: false, tooltip: 'Number of Active Users by Year : Number of active users(profile completed or account created) for the date range(this excludes, deleted, site wide suspended)' },
                { id: "averageNumberOfBids", label: "Average Number of Bids per Item", checked: false, tooltip: 'Average Number of Bids per Item : Total Number of bids / Total Items that have bids for a date range(retract will not be included)' },
            ],
            membershipConst: [
                { id: "membershipCount", label: "Membership Count", checked: false, tooltip: 'Total Subscribers : Total # of all Active Subscribers' },
                { id: "subscriberCount", label: "Subscriber Count", checked: false, tooltip: 'Subscribers : # of Subscriptions For Time Frame Searched' },
                { id: "newSubscriberCount", label: "New Subscriber Count", checked: false, tooltip: 'New Subscribers : # of New Subscriptions For Time Frame Searched' },
                { id: "expiryCount", label: "Expiry Count", checked: false, tooltip: 'Expiring Subscribers : # of Subscriptions That Will Expire During Time Frame Searched' },
                { id: "expiredCount", label: "Expired Count", checked: false, tooltip: 'Expired Subscribers : # of Subscriptions Expired/Ended During Time Frame Searched' },
                { id: "subscriberRevenue", label: "Subscriber Revenue", checked: false, tooltip: 'Subscriber Revenue : $ Amount Paid for Subscriptions During Time Frame Searched' }
            ],
            salesConst: [
                { id: "totalSalesClosed", label: "Total Sales Closed", checked: false, tooltip: 'Total Sales Closed : The total sales closed for the time period selected' },
                { id: "completedOnlineSale", label: "Completed Online Sales Count", checked: false, tooltip: 'Completed Online Sales Count : Total sale count for the selected period' },
                { id: "averageSaleLength", label: "Average Sale Length", checked: false, tooltip: 'Average Sale Length in Days : Total of sale duration for the time period / sale count for the time period' },
                { id: "totalItemsSold", label: "Total Items Sold", checked: false, tooltip: 'Total Items Sold : The total items sold for the time period selected' },
                { id: "totalItemsNotSold", label: "Total Items Not Sold", checked: false, tooltip: 'Total Items Not Sold : The total items not sold for the time period selected' },
                { id: "itemListed", label: "Total Items Listed", checked: false, tooltip: 'Total Items Listed : Number of items that were in Ready status' },
                { id: "itemSoldPercentage", label: "Percentage of Items Sold", checked: false, tooltip: 'Percentage of Items Sold : Total Items Sold / Total Items * 100' },
                { id: "activeLocations", label: "Location Actively using CTBids", checked: false, tooltip: 'Location : Active locations using CTBids' },
                { id: "ownerSelling", label: "Owners Selling on CTBids", checked: false, tooltip: 'Owners Selling on CTBids : Number of Location of sale that is in live or location of sale that is ended for the time period selected' }
            ],
            salesrevenueConst: [
                { id: "ctbidsRevenue", label: "CTBids Site Revenue", checked: false, tooltip: 'CTBids Site Revenue : Total sale price + BP + addon invoice amount + MIR invoice + DG invoice(all paid and unpaid invoice) for the time period selected'},
                { id: "grossSale", label: "Gross Sales", checked: false, tooltip: 'Gross Sales : Item Sold price of all the closed sales for the time period selected' },
                { id: "averageSalePrice", label: "Average Sale Amount", checked: false, tooltip: `Average Sale Amount : The total sale price + buyer's premium / by the total sales closed for the time period selected` },
                { id: "averageItemPrice", label: "Average Item Price", checked: false, tooltip: `Average Item Price : The total sale price + buyer's premium / by the total items sold for the time period selected` },
                { id: "totalBuyersPremium", label: "Total Buyer's Premium", checked: false, tooltip: `Total Buyer's Premium : The total buyer's premium for the time period selected` },
                { id: "totalItemTax", label: "Total Item Tax", checked: false, tooltip: 'Total Item Tax : The total tax on sold items for the time period selected' },
                { id: "totalTax", label: "Total Item Tax + Total BP Tax", checked: false, tooltip: `Total Item Tax + Total BP Tax : The total tax on sold items + total tax on buyer's premium for the time period selected` },
                // { id: "totalSalePrice", label: "Total Sale Price", checked: false },
                // { id: "totalAmount", label: "Total Sale Price + Buyer's Premium", checked: false },
                { id: "averageListingPrice", label: "Average Listing Price", checked: false, tooltip: 'Average Listing Price : Item sold price / Total item listed for the time period selected' },
                // { id: "totalTaxOnBuyerPremium", label: "Total Tax on Buyer's Premium", checked: false },
            ],
            userTableData: [],
            membershipTableData: [],
            salesTableData: [],
            salesRevenueTableData: [],
            userChartLoading: false,
            membershipChartLoading: false,
            salesChartLoading: false,
            salesRevenueChartLoading: false,
            userTableDataLoading: false,
            membershipTableDataLoading: false,
            salesTableDataLoading: false,
            salesRevenueTableDataLoading: false,
            summaryData: [],
            errorpageredirect: false,
            errorType: '',
            dateErrormsg: '',
        }

        this.search = {};
    }

    componentDidMount = () => {
        this.getLocationList();
        window.history.pushState(null, null, window.location.pathname);
        window.addEventListener('popstate', this.onBack);
    }

    onBack = (e) => {
        e.preventDefault();
        window.history.pushState(null, null, window.location.pathname);
    }

    onChangeCheckBox = (event) => {
        let starttime = '';
        let endtime = '';
        if (event == "yesterday") {
            let yesterday = moment().tz('US/Eastern').subtract(1, 'day');
            starttime = yesterday.clone().startOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
            endtime = yesterday.clone().endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
        } else if (event == "week") {
            let dateTime = moment().tz('US/Eastern');
            starttime = dateTime.clone().startOf('isoWeek').utc().format('YYYY-MM-DD HH:mm:ss');
            endtime = dateTime.clone().endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
        } else if (event == "month") {
            let dateTime = moment().tz('US/Eastern');
            starttime = dateTime.clone().startOf('month').utc().format('YYYY-MM-DD HH:mm:ss');
            endtime = dateTime.clone().endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
        } else if (event == "year") {
            let dateTime = moment().tz('US/Eastern');
            starttime = dateTime.clone().startOf('year').utc().format('YYYY-MM-DD HH:mm:ss');
            endtime = dateTime.clone().endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
        } else if (event == "last_7days") {
            let startDateTime = moment().tz('US/Eastern').subtract(7, 'days');
            let endDateTime = moment().tz('US/Eastern').subtract(1, 'day');
            starttime = startDateTime.clone().startOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
            endtime = endDateTime.clone().endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
        } else if (event == "last_30days") {
            let startDateTime = moment().tz('US/Eastern').subtract(30, 'days');
            let endDateTime = moment().tz('US/Eastern').subtract(1, 'day');
            starttime = startDateTime.clone().startOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
            endtime = endDateTime.clone().endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
        } else if (event == "last_365days") {
            let startDateTime = moment().tz('US/Eastern').subtract(365, 'days');
            let endDateTime = moment().tz('US/Eastern').subtract(1, 'day');
            starttime = startDateTime.clone().startOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
            endtime = endDateTime.clone().endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
        } 

        if (event === this.state.isActiveCheckbox) {
            this.setState({
                isActiveCheckbox: '',
                isDateFromCheckBox: false,
                locationcheck: !this.state.search.locationid.value &&  true,
                checkedStartDateTime: '',
                SearchButton: false
            })
        }
        else {
            this.setState({
                isActiveCheckbox: event,
                isDateFromCheckBox: true,
                checkedStartDateTime: starttime,
                checkedEndDateTime: endtime
            }, () => {
                this.setState({
                    ...this.state,
                    issearch: true,
                    locationcheck: false,
                    dateErrormsg:'',
                    search: {
                        ...this.state.search,
                        starttime: {
                            ...this.state.search['starttime'],
                            value: ''
                        },
                        endtime: {
                            ...this.state.search['endtime'],
                            value: ''
                        }
                    },
                    startDate: '',
                    endDate: '',
                    monthDropdownValue: ''
                }, () => {
                    this.searchEnable();
                })
            })
        }
    }

    // Enable search button according to required fields
    searchEnable() {
        let { locationAllSelected } = this.state
        if(Boolean(this.state.search.locationid.value) || locationAllSelected) {
            if(this.state.checkedStartDateTime && this.state.checkedEndDateTime) {
                this.setState({
                    SearchButton: true
                })
            }
            else if(this.state.search.starttime.value && this.state.search.endtime.value) {
                this.setState({
                    SearchButton: true
                })
            } 
            else{
                this.setState({
                    SearchButton: false
                })
            }  
        }
        else {
            this.setState({
                SearchButton: false
            })
        }
    }

    /* called when search input changes */
    searchInputChange = (event) => {
        let eventValue = event.target.value;
        if (event.target.value === 'all') {
            this.setState({
                ...this.state,
                issearch: true,
                isdisabled: true,
                search: {
                    ...this.state.search,
                    [event.target.name]: {
                        ...this.state.search[event.target.name],
                        value: eventValue
                    }
                }
            })
        }
        if (event.target.name === 'locationid') {
            this.setState({
                ...this.state,
                issearch: true,
                isdisabled: true,
                search: {
                    ...this.state.search,
                    [event.target.name]: {
                        ...this.state.search[event.target.name],
                        value: event.target.value === 'All' ? eventValue : Number(eventValue)
                    }
                }
            }, () => {
                if (event.target.value === 'All') {
                    this.setState({
                        locationcheck: (!this.state.isActiveCheckbox && !this.state.dateErrormsg && !this.state.startDate && !this.state.endDate) && true,
                        locationAllSelected: true
                    }, () => {
                        this.searchEnable()
                    })
                }
                else {
                    this.setState({
                        locationcheck: false,
                        locationAllSelected: false
                    }, () => {
                        this.searchEnable()
                    })
                }
                if (event.target.value === '')
                    this.checkSearch();
            });
        }
        else {
            this.setState({
                ...this.state,
                issearch: true,
                search: {
                    ...this.state.search,
                    [event.target.name]: {
                        ...this.state.search[event.target.name],
                        value: eventValue
                    }
                }
            }, () => {
                if (event.target.value === '')
                    this.checkSearch();
            });
        }
    }

    /* called when clear all in search clicked */
    resetState = () => {
        this.setState({
            ...this.state,
            search: JSON.parse(JSON.stringify(this.initialSearchState)),
            locationcheck: false,
            searchData: false,
            issearch: false,
            isdisabled: false,
            isActiveCheckbox: '',
            isDateFromCheckBox: false,
            checkedStartDateTime: '',
            checkedEndDateTime: '',
            locationAllSelected: false,
            SearchButton: false,
            enableSearchData: false,
            errorMsg: false,
            showFilter: false,
            checkedItems: [],
            summaryData: [],
            userConst: this.state.userConst.map(elem => ({ ...elem, checked: false })),
            membershipConst: this.state.membershipConst.map(elem => ({ ...elem, checked: false })),
            salesConst: this.state.salesConst.map(elem => ({ ...elem, checked: false })),
            salesrevenueConst: this.state.salesrevenueConst.map(elem => ({ ...elem, checked: false })),
            tableView: true,
            chartView: false,
            startDate: '',
            endDate: '',
            dateErrormsg: ''
        });
    }

    searchReport = async () => {
        this.setState({
            userTableData: [],
            membershipTableData: [],
            salesTableData: [],
            salesRevenueTableData: [],
            salesChartData: {},
            userChartData: {},
            membershipChartData: {},
            salesRevenueChartData: {}
        })
        let startDate = null
        let endDate =  null

        if(this.state.search.starttime.value){
            let startDateFormat = moment(this.state.search.starttime.value).format("YYYY-MM-DD")
            startDate = moment.tz(startDateFormat + ' 00:00:00', "US/Eastern").utc().format("YYYY-MM-DD hh:mm:ss")
        }
        
        if(this.state.search.endtime.value){
            let endDateformat = moment(this.state.search.endtime.value).format("YYYY-MM-DD")
            endDate = moment.tz(endDateformat + ' 23:59:59', "US/Eastern").utc().format("YYYY-MM-DD hh:mm:ss")
        }

        var filterSearch = {
            filter: [
                {
                    ...this.state.search.locationid,
                    value:( this.state.search.locationid.value && this.state.search.locationid.value !== 'All') ? this.state.search.locationid.value : ""
                },
                {
                    ...this.state.search.starttime,
                    value: this.state.search.starttime.value
                        ? startDate : this.state.isDateFromCheckBox ? this.state.checkedStartDateTime : ''
                },
                {
                    ...this.state.search.endtime,
                    value: this.state.search.endtime.value
                        ? endDate : this.state.isDateFromCheckBox ? this.state.checkedEndDateTime : ''
                },
            ]
        };

        let userFields = [], salesFields = [], membershipFields = [], revenueFields = [];
        let membershipData = '', revenueData = '', userData = '', salesData = '';

        if ( this.state.checkedItems.length === 0 ) {
            userFields = this.state.userConst.map(elem => elem.id);
            salesFields = this.state.salesConst.map(elem => elem.id);
            membershipFields = this.state.membershipConst.map(elem => elem.id);
            revenueFields = this.state.salesrevenueConst.map(elem => elem.id);
        } else {
            userFields = this.state.userConst.filter(elem => elem.checked).map(elem => elem.id);
            salesFields = this.state.salesConst.filter(elem => elem.checked).map(elem => elem.id);
            membershipFields = this.state.membershipConst.filter(elem => elem.checked).map(elem => elem.id);
            revenueFields = this.state.salesrevenueConst.filter(elem => elem.checked).map(elem => elem.id);
        }

        const userFilter = {
            field: userFields,
            filter: filterSearch.filter,
        };

        const salesFilter = {
            field: salesFields,
            filter: filterSearch.filter,
        };

        const membershipFilter = {
            field: membershipFields,
            filter: filterSearch.filter,
        };

        const salesRevenueFilter = {
            field: revenueFields,
            filter: filterSearch.filter,
        };

        try {
            if(this.state.tableView) {
                this.setState({
                    showFilter: false,
                    enableSearchData: true,
                })

                if (membershipFilter.field.length > 0) {
                    this.setState({
                        membershipTableDataLoading: true
                    })
                    axios.post(getSellerUrl(membershipSummaryReport, '', true), membershipFilter, getSellerHeaders()).then(membershipRes => {
                        this.setState({
                            membershipTableData: membershipRes?.data.data[0].membership,
                            membershipTableDataLoading: false
                        })
                    }).catch(error => error);
                }

                if (salesRevenueFilter.field.length > 0) {
                    this.setState({
                        salesRevenueTableDataLoading: true
                    })
                    axios.post(getSellerUrl(revenueSummaryReport, '', true), salesRevenueFilter, getSellerHeaders()).then(revenueRes => {
                        this.setState({
                            salesRevenueTableData: revenueRes?.data.data[0].revenue,
                            salesRevenueTableDataLoading: false
                        })
                    }).catch(error => error);
                }

                if (userFilter.field.length > 0) {
                    this.setState({
                        userTableDataLoading: true
                    })
                    axios.post(getSellerUrl(userSummaryReport, '', true), userFilter, getSellerHeaders()).then(userRes => {
                        this.setState({
                            userTableData: userRes?.data.data[0].user,
                            userTableDataLoading: false
                        })
                    }).catch(error => error);
                }

                if (salesFilter.field.length > 0) {
                    this.setState({
                        salesTableDataLoading: true
                    })
                    axios.post(getSellerUrl(salesSummaryReport, '', true), salesFilter, getSellerHeaders()).then(salesRes => {
                        this.setState({
                            salesTableData: salesRes?.data.data[0].sale,
                            salesTableDataLoading: false
                        })
                    }).catch(error => error);
                }

            } else if (this.state.chartView) {

                this.setState({
                    showFilter: false,
                    enableSearchData: true
                })

                if (membershipFilter.field.length > 0) {
                    this.setState({
                        membershipChartLoading: true
                    })
                    axios.post(getSellerUrl(membershipConsolidateReport, '', true), membershipFilter, getSellerHeaders()).then(membershipRes => {
                        membershipData = membershipRes?.data?.status == 'success' ? membershipRes.data.data : null;
                        this.setState({
                            membership: membershipData,
                            membershipChartData: membershipData?.length > 0 && membershipFields.length > 0 ? membershipBarChartData(membershipData) : null,
                            membershipChartLoading: false
                        })
                    }).catch(error => error);
                }
                
                if (salesRevenueFilter.field.length > 0) {
                    this.setState({
                        salesRevenueLoading: true
                    })
                    axios.post(getSellerUrl(revenueConsolidateReport, '', true), salesRevenueFilter, getSellerHeaders()).then(revenueRes=> {
                        revenueData = revenueRes?.data?.status == 'success' ? revenueRes.data.data : null;
                        this.setState({
                            revenue: revenueData,
                            salesRevenueChartData: revenueData?.length > 0 && revenueFields.length > 0 ? salesRevenueBarChartData(revenueData) : null,
                            salesRevenueChartLoading: false
                        })
                    }).catch(error => error);
                }
                
                if (userFilter.field.length > 0) {
                    this.setState({
                        userChartLoading: true
                    })
                    axios.post(getSellerUrl(userConsolidateReport, '', true), userFilter, getSellerHeaders()).then(userRes => {
                        userData = userRes?.data?.status == 'success' ? userRes.data.data : null;
                        this.setState({
                            user: userData,
                            userChartData: userData?.length > 0 && userFields.length > 0 ? userBarChartData(userData) : null,
                            userChartLoading: false
                        })
                    }).catch(error => error);
                }
                
                if (salesFilter.field.length > 0) {
                    this.setState({
                        salesChartLoading: true
                    })
                    axios.post(getSellerUrl(salesConsolidateReport, '', true), salesFilter, getSellerHeaders()).then(salesRes => {
                        salesData = salesRes?.data?.status == 'success' ? salesRes.data.data : null;
                        this.setState({
                            sales: salesData,
                            salesChartData: salesData?.length > 0 && salesFields.length > 0 ? salesBarChartData(salesData) : null,
                            salesChartLoading: false
                        })
                    }).catch(error => error);
                }
            }   
        } catch (error) {
            this.setState({
                enableSearchData: false,
                errorMsg: true
            });
        }
    }

    onclickCSV = () => {
        //set admin Report data
        let csvRow = ""
        let csvHeader = ""
        let columns = [
            { name: 'Total Users', value: this.state.userTableData.totalUsers ? this.state.userTableData.totalUsers : '0' },
            { name: 'Total Registered Users', value: this.state.userTableData.registeredUsers ? this.state.userTableData.registeredUsers : '0' },
            { name: 'Profile Completed', value: this.state.userTableData.profileCompleted ? this.state.userTableData.profileCompleted : '0' },
            { name: 'Account Created', value: this.state.userTableData.accountCreated ? this.state.userTableData.accountCreated : '0' },
            { name: 'Suspended Users', value: this.state.userTableData.suspendedUsers ? this.state.userTableData.suspendedUsers : '0' },
            { name: "Number of Repeat Purchasers", value: this.state.userTableData.repeatedPurchaser ? this.state.userTableData.repeatedPurchaser : '0' },
            { name: "Number of New Purchasers", value: this.state.userTableData.newPurchaser ? this.state.userTableData.newPurchaser : '0' },
            { name: "Number of Active Users", value: this.state.userTableData.activeUsers ? this.state.userTableData.activeUsers : '0' },
            { name: 'Average Number Of Bid', value: this.state.userTableData.averageNumberOfBids ? this.state.userTableData.averageNumberOfBids : '0' },

            { name: 'Total Subscribers', value: this.state.membershipTableData.membershipCount ? this.state.membershipTableData.membershipCount : '0' },
            { name: 'Subscribers', value: this.state.membershipTableData.subscriberCount ? this.state.membershipTableData.subscriberCount : '0' },
            { name: 'New Subscribers ', value: this.state.membershipTableData.newSubscriberCount ? this.state.membershipTableData.newSubscriberCount : '0' },
            { name: 'Expiring Subscribers', value: this.state.membershipTableData.expiryCount ? this.state.membershipTableData.expiryCount : '0' },
            { name: 'Expired Subscribers', value: this.state.membershipTableData.expiredCount ? this.state.membershipTableData.expiredCount : '0' },
            { name: 'Subscriber Revenue', value: this.state.membershipTableData.subscriberRevenue ? '$ ' + this.state.membershipTableData.subscriberRevenue : '$ 0.00' },

            { name: 'Total Sales Closed', value: this.state.salesTableData.totalSalesClosed ? this.state.salesTableData.totalSalesClosed : '0' },
            { name: 'Total Items Sold', value: this.state.salesTableData.totalItemsSold ? this.state.salesTableData.totalItemsSold : '0' },
            { name: 'Total Items Not Sold', value: this.state.salesTableData.totalItemsNotSold ? this.state.salesTableData.totalItemsNotSold : '0' },
            { name: 'Average Sale Length', value: this.state.salesTableData.averageSaleLength ? this.state.salesTableData.averageSaleLength : '0' },
            { name: 'Completed Online Sales Count', value: this.state.salesTableData.completedOnlineSale ? this.state.salesTableData.completedOnlineSale : '0' },
            { name: 'Percentage of Items Sold', value: this.state.salesTableData.itemSoldPercentage ? this.state.salesTableData.itemSoldPercentage : '0' },
            { name: 'Total Items Listed', value: this.state.salesTableData.itemListed ? this.state.salesTableData.itemListed : '0' },
            { name: 'Location Actively using CTBids', value: this.state.salesTableData.activeLocations ? this.state.salesTableData.activeLocations : '0' },
            { name: 'Owners Selling on CTBids', value: this.state.salesTableData.ownerSelling ? this.state.salesTableData.ownerSelling : '0' },

            { name: 'CTBids Site Revenue', value: this.state.salesRevenueTableData.ctbidsRevenue ? '$ ' + this.state.salesRevenueTableData.ctbidsRevenue : '$ 0.00' },
            { name: 'Gross Sales', value: this.state.salesRevenueTableData.grossSale ? '$ ' + this.state.salesRevenueTableData.grossSale : '$ 0.00' },
            { name: 'Average Sale Amount', value: this.state.salesRevenueTableData.averageSalePrice ? '$ ' + this.state.salesRevenueTableData.averageSalePrice : '$ 0.00' },
            { name: 'Average Item Price', value: this.state.salesRevenueTableData.averageItemPrice ? '$ ' + this.state.salesRevenueTableData.averageItemPrice : '$ 0.00' },
            { name: 'Average Listing Price', value: this.state.salesRevenueTableData.averageListingPrice ? '$ ' + this.state.salesRevenueTableData.averageListingPrice : '$ 0.00' },
            { name: 'Total Buyers Premium', value: this.state.salesRevenueTableData.totalBuyersPremium ? '$ ' + this.state.salesRevenueTableData.totalBuyersPremium : '$ 0.00' },
            { name: 'Total Item Tax', value: this.state.salesRevenueTableData.totalItemTax ? '$ ' + this.state.salesRevenueTableData.totalItemTax : '$ 0.00' },
            { name: 'Total Item Tax + Total BP Tax', value: this.state.salesRevenueTableData.totalTax ? '$ ' + this.state.salesRevenueTableData.totalTax : '$ 0.00' },
            // { name: 'Total Sale Price', value: this.state.summaryData[0].totalSalePrice ? '$ ' + this.state.summaryData[0].totalSalePrice : '' },
            // { name: 'Total Sale Price + Buyers Premium', value: this.state.summaryData[0].totalAmount ? '$ ' + this.state.summaryData[0].totalAmount : '' },
        ]
        let csvHeaderItem = []
        let csvRowItem = []
        columns.map((col, key) => {
            csvHeaderItem.push(col["name"])
            typeof col["value"] === 'string' ? csvRowItem.push('"' + col["value"].replace(/"/g, '""') + '"') : csvRowItem.push(col["value"]);
        })
        csvHeader = csvHeaderItem.map(elem => { return '"' + elem + '"' }).join(",") + '\r\n'
        csvRow = csvRow + csvRowItem.join(",") + '\r\n'

        //csv download section
        let BOM = "\uFEFF";
        let csvData = BOM + csvHeader + csvRow
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.setAttribute('href', url)
        a.setAttribute('download', `admin-report.csv`);
        a.click()
    }

    /* check enable search and clear all */
    checkSearch = () => {
        let isenable = false;
        let issearchData = this.state.searchData;
        for (let control of Object.keys(this.state.search)) {
            if (this.state.search[control].value) {
                isenable = true;
                break;
            }
        }
        this.setState({
            ...this.state,
            search: isenable ? this.state.search : this.initialSearchState,
            searchData: isenable ? this.state.searchData : false,
            issearch: isenable
        }, () => {
            if (!isenable && issearchData)
                this.dataTable.current.searchData();
        });
    }

    /* set date for date input change */
    setDate = (control, searchKey, date) => {
        let checkLocation;
        if (this.state.search.locationid.value) {
            checkLocation = true;
        }
    
        this.setState({
            ...this.state,
            issearch: true,
            [control]: date,
            search: {
                ...this.state.search,
                [searchKey]: {
                    ...this.state.search[searchKey],
                    value: moment(date).format('YYYY-MM-DD HH:mm:ss')
                },
            }
        }, () => {
            let dateErrormsg = '';  
            const { starttime, endtime } = this.state.search;
            if (starttime.value && endtime.value) {
                const start = new Date(starttime.value);
                const end = new Date(endtime.value);
    
                const monthsDiff =
                    (end.getFullYear() - start.getFullYear()) * 12 +
                    (end.getMonth() - start.getMonth());
    
                    if (end < start) {
                        dateErrormsg = "Please select a date range within any 12 month period";
                        this.setState({
                            ...this.state,
                            endDate: null,
                        })
            
                    }
                else if (monthsDiff > 12 || (monthsDiff === 12 && end.getDate() > start.getDate())) {
                    dateErrormsg = "Please select a date range within any 12 month period";
                }
            }
    
            let erroMsg = ''
            let locationcheck = false

            if( typeof this.state.search.locationid.value === 'string'){
                 locationcheck = true
                if (this.state.search.locationid.value && this.state.search.endtime.value && this.state.search.starttime.value) {
                    locationcheck = false
                }
            }
    
            this.setState(prev => ({
                ...prev,
                dateErrormsg,
                locationcheck,
                isActiveCheckbox: '',
                isDateFromCheckBox: false,
                checkedStartDateTime: '',
                checkedEndDateTime: ''
            }), () => {
                this.searchEnable();
            });
        });
    }
    
    handleDateChangeRaw = (e) => {
        e.preventDefault();
    }

    /* Get Location list */
    getLocationList = async () => {
        await axios.all([
            await axios.post(getSellerUrl(locationlist), JSON.stringify({ field: ['id', 'title'] }), getSellerHeaders()),
        ])
            .then(response => {
                this.locations = response[0].data.data;
                if (this.locations.length == '1') {
                    this.setState({
                        ...this.state,
                        locations: ArrayValueReorder(response[0].data.data, 'title'),
                        location: this.locations[0].title,
                        locationlength: this.locations.length,
                        isdisabled: true,
                        search: {
                            ...this.state.search,
                            locationid: {
                                ...this.state.search.locationid,
                                value: this.locations[0].id
                            },
                        }
                    });
                }
                else {
                    this.setState({
                        ...this.state,
                        locations: ArrayValueReorder(response[0].data.data, 'title'),
                        location: this.locations[0].title,
                        locationlength: this.locations.length,
                    })
                }
            })
            .catch(error => {
                if(error.response?.status == 401 || error.response?.status == 404) {
                    this.setState({
                        errorpageredirect: true,
                        errorType: error.response.status == 401 ? 'expiryError' : error.response.status == 404 ? 'noData' : ''
                    })
                }
            });
    }

    leadershipReportView = (type) => {
        if (type == 'chart' && !this.state.chartView) {
            this.setState({
                chartView: true,
                tableView: false
            }, () => {
                this.searchReport();
            })
        } else if (type == 'table' && !this.state.tableView) {
            this.setState({
                chartView: false,
                tableView: true
            }, () => {
                this.searchReport();
            })
        }
    }

    getDateRange = (event) => {
        const monthSelected = event.target.value;
        const fromDate = moment().clone().subtract(monthSelected, 'months').format('YYYY-MM-DD HH:mm:ss');
        const toDate = moment().format('YYYY-MM-DD HH:mm:ss');
        this.setState({
            monthDropdownValue: monthSelected,
            search: {
                ...this.state.search,
                starttime: {
                    ...this.state.search['starttime'],
                    value: fromDate
                },
                endtime: {
                    ...this.state.search['endtime'],
                    value: toDate
                }
            }
        })
    }

    // Handle checkbox change
    handleCheckboxChange = (type, index, event) => {  
        let data = this.state[type];
        if (event.target.checked) {
            data[index].checked = event.target.checked;
            this.state.checkedItems = [...this.state.checkedItems, event.target.id];
        } else {
            data[index].checked = event.target.checked;
            if (!event.target.checked) {
                this.state.checkedItems = this.state.checkedItems.filter(item => item !== data[index].id);
            }
        }
        
        this.setState({
            checkedItems: [
                ...this.state.checkedItems
            ],
            [type]: data
        })
    };

    showFilterData = () => {
        this.setState({
            showFilter: true
        })
    }

    formatPrice = (data) => {
        const formattedPrice = parseFloat(data).toFixed(2);
        return formattedPrice.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    render() {
        if (this.state.errorpageredirect) {
            return <NoData {...this.props} type={this.state.errorType} />
        }
        return (
            <div style={{ overflow: "auto", overflowY: "overlay" }} id="sale-hostory-report">
                <div className="sale-search">
                    <div className="sales-list-search">
                        <Form className="sales-list-font">
                            <div className="sales-search-container">
                                <Row className='d-flex align-items-center'>
                                    <Col>
                                        <label className='text-nowrap'>Location *</label>
                                        <Form.Control as="select" name="locationid" value={this.state.search.locationid.value} onKeyDown={this._handleKeyDown} onChange={(event) => this.searchInputChange(event)}>
                                            <option value="" disabled>Please select location</option>
                                            {this.state.locationlength > 1 ? <option value="All">All</option> : ''}
                                            {this.state.locations.map((loc, index) =>
                                                <option key={index} value={loc.id}>{loc.title}</option>
                                            )}
                                        </Form.Control>
                                    </Col>
                                    <Col>
                                        <label className='text-nowrap'>Sale End Date From *</label>
                                        <div>
                                            <label style={{ width: "100%" }} onClick={e => this.start.state.open && e.preventDefault()}>
                                                <DatePicker 
                                                ref={r => this.start = r} 
                                                className="datePicker" 
                                                dateFormat="MM/dd/yyyy" 
                                                value={this.state.startDate} 
                                                selected={this.state.startDate} 
                                                onChange={date => { this.setDate('startDate', 'starttime', date) }} 
                                                onChangeRaw={this.handleDateChangeRaw} 
                                                peekNextMonth 
                                                showMonthDropdown 
                                                showYearDropdown 
                                                placeholderText="MM/DD/YYYY" 
                                                dropdownMode="select" />
                                                <i style={{ position: "absolute", top: "52%", right: "12%", color: "#00263E" }} className="fa fa-calendar" aria-hidden="true"></i>
                                            </label>
                                        </div>
                                    </Col>
                                    <Col>
                                        <label className='text-nowrap'>Sale End Date To *</label>
                                        <div>
                                            <label style={{ width: "100%" }} onClick={e => this.end.state.open && e.preventDefault()}>
                                                <DatePicker 
                                                minDate={this.state.startDate ? this.state.startDate : ''} 
                                                ref={r => this.end = r} 
                                                className="datePicker" 
                                                dateFormat="MM/dd/yyyy" 
                                                value={this.state.endDate} 
                                                selected={this.state.endDate} 
                                                onChange={date => { this.setDate('endDate', 'endtime', date) }} 
                                                onChangeRaw={this.handleDateChangeRaw} 
                                                peekNextMonth 
                                                showMonthDropdown 
                                                showYearDropdown 
                                                placeholderText="MM/DD/YYYY" 
                                                dropdownMode="select" />
                                                <i style={{ position: "absolute", top: "52%", right: "12%", color: "#00263E" }} className="fa fa-calendar" aria-hidden="true"></i>
                                            </label>
                                        </div>
                                    </Col>
                                    <Col>
                                        <div className="mt-3 d-flex justify-content-end">
                                            <Button className="clear-button clickable" onClick={() => this.resetState()}>Clear All</Button>
                                            <Button variant="seller-search clickable" disabled={!this.state.SearchButton || this.state.dateErrormsg} onClick={() => this.searchReport()}>Search</Button>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className='checkbox-resp' style={{ width: '100%'}}>
                                   <div className='d-flex wt-50'>
                                        <Col>
                                                <div className={`d-flex align-items-center header`}>
                                                    <div style={{ marginLeft: '10px' }}>
                                                        <div className={`custom-checkbox-div ${this.state.isActiveCheckbox === 'yesterday'? 'checked' : ''}`} onClick={() => this.onChangeCheckBox('yesterday')}></div>
                                                    </div>
                                                    <div style={{ marginLeft: '5%'}}>
                                                        <div className='text-nowrap'>Yesterday</div>
                                                    </div>
                                                    <div style={{ marginLeft: '4%', marginTop: '1.5px'}}>
                                                    <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip-clone" style={{ visibility: 'visible' }}>{'Yesterday'}</Tooltip>}>
                                                            <i className={`fa fa-question-circle`} style={{ fontSize: "14px" }}></i> 
                                                        </OverlayTrigger>
                                                    </div>
                                                </div>
                                        </Col>
                                        <Col>
                                                <div className={`d-flex align-items-center header`}>
                                                    <div style={{ marginLeft: '10px'}}>
                                                    <div className={`custom-checkbox-div ${this.state.isActiveCheckbox === 'week' ? 'checked' : ''}`} onClick={() => this.onChangeCheckBox('week')}></div>
                                                    </div>
                                                    <div style={{ marginLeft: '5%'}}>
                                                        <div className='text-nowrap'>Week to Date</div>
                                                    </div>
                                                    <div style={{ marginLeft: '4%', marginTop: '1.5px'}}>
                                                    <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip-clone" style={{ visibility: 'visible' }}>{'Monday to today of current week'}</Tooltip>}>
                                                            <i className={`fa fa-question-circle`} style={{ fontSize: "14px" }}></i> 
                                                        </OverlayTrigger>
                                                    </div>
                                                </div>
                                        </Col>
                                   </div>
                                   <div className='d-flex wt-50'>
                                        <Col>
                                                <div className={`d-flex align-items-center header`}>
                                                    <div style={{ marginLeft: '10px'}}>
                                                        <div className={`custom-checkbox-div ${this.state.isActiveCheckbox === 'month'? 'checked' : ''}`} onClick={() => this.onChangeCheckBox('month')}></div>
                                                    </div>
                                                    <div style={{ marginLeft: '5%'}}>
                                                        <div className='text-nowrap'>Month to Date</div>
                                                    </div>
                                                    <div style={{ marginLeft: '4%', marginTop: '1.5px'}}>
                                                    <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip-clone" style={{ visibility: 'visible' }}>{'First day of the current month to current date'}</Tooltip>}>
                                                            <i className={`fa fa-question-circle`} style={{ fontSize: "14px" }}></i> 
                                                        </OverlayTrigger>
                                                    </div>
                                                </div>
                                        </Col>
                                        <Col>
                                                <div className={`d-flex align-items-center header`}>
                                                    <div style={{ marginLeft: '10px'}}>
                                                    <div className={`custom-checkbox-div ${this.state.isActiveCheckbox === 'year'? 'checked' : ''}`} onClick={() => this.onChangeCheckBox('year')}></div>
                                                    </div>
                                                    <div style={{ marginLeft: '5%'}}>
                                                        <div className='text-nowrap'>Year to Date</div>
                                                    </div>
                                                    <div style={{ marginLeft: '4%', marginTop: '1.5px'}}>
                                                    <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip-clone" style={{ visibility: 'visible' }}>{'First day of the current year to current date'}</Tooltip>}>
                                                            <i className={`fa fa-question-circle`} style={{ fontSize: "14px" }}></i> 
                                                        </OverlayTrigger>
                                                    </div>
                                                </div>
                                        </Col>
                                   </div>
                                </Row>

                                <Row className='checkbox-resp' style={{ width: '100%'}}>
                                    <div className='d-flex wt-50'>
                                        <Col>
                                                <div className={`d-flex align-items-center header`}>
                                                    <div style={{ marginLeft: '10px' }}>
                                                        <div className={`custom-checkbox-div ${this.state.isActiveCheckbox === 'last_7days'? 'checked' : ''}`} onClick={() => this.onChangeCheckBox('last_7days')}></div>
                                                    </div>
                                                    <div style={{ marginLeft: '5%'}}>
                                                        <div className='text-nowrap'>Last 7 days</div>
                                                    </div>
                                                    <div style={{ marginLeft: '4%', marginTop: '1.5px'}}>
                                                    <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip-clone" style={{ visibility: 'visible' }}>{'Past 7 days excluding current date'}</Tooltip>}>
                                                            <i className={`fa fa-question-circle`} style={{ fontSize: "14px" }}></i> 
                                                        </OverlayTrigger>
                                                    </div>
                                                </div>
                                        </Col>
                                        <Col>
                                                <div className={`d-flex align-items-center header`}>
                                                    <div style={{ marginLeft: '10px'}}>
                                                    <div className={`custom-checkbox-div ${this.state.isActiveCheckbox === 'last_30days'? 'checked' : ''}`} onClick={() => this.onChangeCheckBox('last_30days')}></div>
                                                    </div>
                                                    <div style={{ marginLeft: '5%'}}>
                                                        <div className='text-nowrap'>Last 30 days</div>
                                                    </div>
                                                    <div style={{ marginLeft: '4%', marginTop: '1.5px'}}>
                                                    <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip-clone" style={{ visibility: 'visible' }}>{'Past 30 days excluding current date'}</Tooltip>}>
                                                            <i className={`fa fa-question-circle`} style={{ fontSize: "14px" }}></i> 
                                                        </OverlayTrigger>
                                                    </div>
                                                </div>
                                        </Col>
                                    </div>
                                    <div className='d-flex wt-50'>
                                        <Col>
                                            <div className={`d-flex align-items-center header`}>
                                                <div style={{ marginLeft: '10px'}}>
                                                    <div className={`custom-checkbox-div ${this.state.isActiveCheckbox === 'last_365days'? 'checked' : ''}`} onClick={() => this.onChangeCheckBox('last_365days')}></div>
                                                </div>
                                                <div style={{ marginLeft: '5%'}}>
                                                    <div className='text-nowrap'>Last 365 days</div>
                                                </div>
                                                <div style={{ marginLeft: '4%', marginTop: '1.5px'}}>
                                                <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip-clone" style={{ visibility: 'visible' }}>{'Past 365 days excluding current date'}</Tooltip>}>
                                                        <i className={`fa fa-question-circle`} style={{ fontSize: "14px" }}></i> 
                                                    </OverlayTrigger>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col></Col>
                                    </div>
                                </Row>  

                                <div className='d-flex' style={{ marginLeft: "10px", marginTop: "3%" }} onClick={() => {this.showFilterData()}}>
                                    <img className="clickable" src={`${process.env.PUBLIC_URL}/static/images/filter.png`} alt='filter-img' style={{ width: "20px", height: "20px" }} />
                                    <label className='font-weight-bold ml-2 clickable'>Select Data Elements</label>
                                </div>                                
                                {
                                    this.state.showFilter ?
                                        <div className='w-100 mt-4 d-flex justify-content-between mobile-column-resp'>
                                            <Row className='w-100 d-flex flex-column' style={{ width: "25%" }}>
                                                <div className="font-weight-bold ml-3 mb-3" style={{ width: "25%", color: "#a50050" }}>Users: </div>
                                                {
                                                    this.state.userConst.map((item, index) => (
                                                        <Col>
                                                            <div key={item.id}>
                                                                <div className='d-flex flex-row align-items-center'>
                                                                    <input
                                                                        type="checkbox"
                                                                        id={item.id}
                                                                        className='mr-2 clickable'
                                                                        name={item.label}
                                                                        checked={item.checked}
                                                                        onChange={(event) => {this.handleCheckboxChange('userConst', index, event)}}
                                                                    />
                                                                    <span style={{ whiteSpace: "nowrap" }}>{item.label}</span>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    ))
                                                }
                                            </Row>
                                            <Row className='w-100 d-flex flex-column' style={{ width: "25%" }}>
                                                <div className="font-weight-bold ml-3 mb-3" style={{ width: "25%", color: "#a50050" }}>Membership: </div>
                                                {
                                                    this.state.membershipConst.map((item, index) => (
                                                        <Col>
                                                            <div key={item.id}>
                                                                <div className='d-flex flex-row align-items-center'>
                                                                    <input
                                                                        type="checkbox"
                                                                        id={item.id}
                                                                        className='mr-2 clickable'
                                                                        name={item.label}
                                                                        checked={item.checked}
                                                                        onChange={(event) => {this.handleCheckboxChange('membershipConst', index, event)}}
                                                                    />
                                                                    <span style={{ whiteSpace: "nowrap" }}>{item.label}</span>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    ))
                                                }
                                                <Col></Col>
                                                <Col></Col>
                                                <Col></Col>
                                                <Col></Col>
                                            </Row>
                                            <Row className='w-100 d-flex flex-column' style={{ width: "25%" }}>
                                                <div className="font-weight-bold ml-3 mb-3" style={{ width: "25%", color: "#a50050" }}>Sales: </div>
                                                {
                                                    this.state.salesConst.map((item, index) => (
                                                        <Col>
                                                            <div key={item.id}>
                                                                <div className='d-flex flex-row align-items-center'>
                                                                    <input
                                                                        type="checkbox"
                                                                        id={item.id}
                                                                        className='mr-2 clickable'
                                                                        name={item.label}
                                                                        checked={item.checked}
                                                                        onChange={(event) => {this.handleCheckboxChange('salesConst', index, event)}}
                                                                    />
                                                                    <span style={{ whiteSpace: "nowrap" }}>{item.label}</span>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    ))
                                                }
                                                <Col></Col>
                                                <Col></Col>
                                                <Col></Col>
                                            </Row>
                                            <Row className='w-100 d-flex flex-column'>
                                                <div className="font-weight-bold ml-3 mb-3" style={{ color: "#a50050" }}>Sales Revenue: </div>
                                                {
                                                    this.state.salesrevenueConst.map((item, index) => (
                                                        <Col>
                                                            <div key={item.id}>
                                                                <div className='d-flex flex-row align-items-center'>
                                                                    <input
                                                                        type="checkbox"
                                                                        id={item.id}
                                                                        className='mr-2 clickable'
                                                                        name={item.label}
                                                                        checked={item.checked}
                                                                        onChange={(event) => {this.handleCheckboxChange('salesrevenueConst', index, event)}}
                                                                    />
                                                                    <span style={{ whiteSpace: "nowrap" }}>{item.label}</span>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    ))
                                                } 
                                                <Col></Col>
                                            </Row>
                                        </div> : ''
                                }
                                <div className='text-danger text-center'>
                                    {(this.state.locationcheck || this.state.dateErrormsg) &&
                                        <div  style={{fontSize: '14px'}}> {this.state.locationcheck ? this.errorMsg : this.state.dateErrormsg}</div>
                                    }
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>
                {
                    this.state.enableSearchData ?
                        <>
                            <div className='mt-4' >
                                <div className='d-flex justify-content-end mt-3'>
                                    <Button className={`admin-report-menu-btn ${this.state.tableView ? 'active' : ''}`} disabled={!this.state.SearchButton} onClick={() => this.leadershipReportView('table')}>Table view</Button>
                                    <Button className={`admin-report-menu-btn ${this.state.chartView ? 'active' : ''}`} disabled={!this.state.SearchButton} onClick={() => this.leadershipReportView('chart')}>Chart View</Button>
                                </div>
                                {
                                    this.state.tableView ? <>
                                    <div>
                                        <div style={{ float: 'right' }}>
                                            <div >
                                                <Button onClick={() => this.onclickCSV()} className="button-view" style={{ float: "right" }}>
                                                    <div className="button-text" >
                                                        <img src={Excel} width="25px" height="25px" alt="excel" />Export to CSV</div>
                                                </Button>
                                            </div>
                                        </div>
                                            {
                                                this.state.userTableDataLoading ?
                                                    <div className="d-flex justify-content-center align-items-center" style={{ height: "54vh" }}>
                                                        <img src={`${process.env.PUBLIC_URL}/static/icons/table/option_1.gif`} alt="default" height="100px" width="100px" />
                                                    </div> : <div className='p-3' style={{ backgroundColor: '#BBC7D6' }}>
                                                        <div className="mb-3 mt-3" style={{ fontWeight: 'bold', fontSize: '16px', textAlign: 'left' }}>
                                                            Users :
                                                            <span className='border-set' style={{ width: '50px' }}></span>
                                                        </div>
                                                        <div className='admin-report'>
                                                            <Row className='mb-3 mt-3 sections gap-2' >
                                                                {
                                                                    this.state.userConst.map((user, uindex) => (
                                                                        <div className='d-flex flex-column section-column' >
                                                                            <div className='mb-2'><span style={{ marginRight: "5px", fontWeight: 'bold' }}>{user.label}</span>
                                                                                <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip-clone" style={{ visibility: 'visible' }}>{user.tooltip}</Tooltip>}>
                                                                                    <i className={`fa fa-question-circle`} style={{ fontSize: "14px" }}></i>
                                                                                </OverlayTrigger>
                                                                            </div>
                                                                            <div style={{ whiteSpace: 'nowrap', marginBottom: "10px" }}>{Object.keys(this.state?.userTableData).length > 0 ? this.state?.userTableData?.[user.id] || this.state?.userTableData?.[user.id] == 0 ? this.state?.userTableData?.[user.id] : '- - -' : '- - -'}</div>
                                                                        </div>
                                                                    ))
                                                                }
                                                            </Row>
                                                        </div>
                                                    </div>
                                            }
                                            {
                                                this.state.membershipTableDataLoading ?
                                                    <div className="d-flex justify-content-center align-items-center" style={{ height: "54vh" }}>
                                                        <img src={`${process.env.PUBLIC_URL}/static/icons/table/option_1.gif`} alt="default" height="100px" width="100px" />
                                                    </div> : <div className='p-3 mt-4' style={{ backgroundColor: '#BBC7D6' }}>
                                                        <div className="mb-3 mt-3" style={{ fontWeight: 'bold', fontSize: '16px', textAlign: 'left' }}>
                                                            Membership :
                                                            <span className='border-set' style={{ width: '100px' }}></span>
                                                        </div>
                                                        <div className='admin-report' style={{ backgroundColor: '#BBC7D6' }}>
                                                            <Row className='mb-3 mt-3 sections gap-2' >
                                                                {
                                                                    this.state.membershipConst.map((member, uindex) => (
                                                                        <div className='d-flex flex-column section-column'>
                                                                            <div className='mb-2'><span style={{ marginRight: '5px', fontWeight: 'bold' }}>{member.label}</span>
                                                                                <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip-clone" style={{ visibility: 'visible' }}>{member.tooltip}</Tooltip>}>
                                                                                    <i className={`fa fa-question-circle`} style={{ fontSize: "14px" }}></i>
                                                                                </OverlayTrigger>
                                                                            </div>
                                                                            <div style={{ whiteSpace: 'nowrap', marginBottom: "10px" }}>{Object.keys(this.state?.membershipTableData).length > 0 ? this.state?.membershipTableData?.[member.id] || this.state?.membershipTableData?.[member.id] == 0 ? member.id == "subscriberRevenue" ? '$ ' + this.formatPrice(this.state?.membershipTableData?.[member.id]) : this.state?.membershipTableData?.[member.id] : '- - -' : '- - -'}</div>
                                                                        </div>
                                                                    ))
                                                                }
                                                            </Row>
                                                        </div>
                                                    </div>
                                            }
                                            {
                                                this.state.salesTableDataLoading ?
                                                    <div className="d-flex justify-content-center align-items-center" style={{ height: "54vh" }}>
                                                        <img src={`${process.env.PUBLIC_URL}/static/icons/table/option_1.gif`} alt="default" height="100px" width="100px" />
                                                    </div> : <div className='p-3 mt-4' style={{ backgroundColor: '#BBC7D6' }}>
                                                        <div className="mb-3 mt-3" style={{ fontWeight: 'bold', fontSize: '16px', textAlign: 'left' }}>
                                                            Sales :
                                                            <span className='border-set' style={{ width: '50px' }}></span>
                                                        </div>
                                                        <div className='admin-report' >
                                                            <Row className='mb-3 mt-3 sections gap-2' >
                                                                {
                                                                    this.state.salesConst.map((sale, uindex) => (
                                                                        <div className='d-flex flex-column section-column'>
                                                                            <div className='mb-2'><span style={{ marginRight: '5px', fontWeight: 'bold' }}>{sale.label}</span>
                                                                                <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip-clone" style={{ visibility: 'visible' }}>{sale.tooltip}</Tooltip>}>
                                                                                    <i className={`fa fa-question-circle`} style={{ fontSize: "14px" }}></i>
                                                                                </OverlayTrigger>
                                                                            </div>
                                                                            <div style={{ whiteSpace: 'nowrap', marginBottom: "10px" }}>{Object.keys(this.state?.salesTableData).length > 0 ? this.state?.salesTableData || this.state?.salesTableData?.[sale.id] == 0 ? sale.id == "itemSoldPercentage" ? this.state?.salesTableData?.[sale.id] + ' %' : this.state?.salesTableData?.[sale.id] : '- - -' : '- - -'}</div>
                                                                        </div>
                                                                    ))
                                                                }
                                                            </Row>
                                                        </div>
                                                    </div>
                                            }
                                            {
                                                this.state.salesRevenueTableDataLoading ?
                                                    <div className="d-flex justify-content-center align-items-center" style={{ height: "54vh" }}>
                                                        <img src={`${process.env.PUBLIC_URL}/static/icons/table/option_1.gif`} alt="default" height="100px" width="100px" />
                                                    </div> : <div className='p-3 mt-4' style={{ backgroundColor: '#BBC7D6' }}>
                                                        <div className="mb-3 mt-3" style={{ fontWeight: 'bold', fontSize: '16px', textAlign: 'left' }}>
                                                            Sales Revenue :
                                                            <span className='border-set' style={{ width: '120px' }}></span>
                                                        </div>
                                                        <div className='admin-report'>
                                                            <Row className='mb-3 mt-3 sections gap-2' >
                                                                {
                                                                    this.state.salesrevenueConst.map((revenue, uindex) => (
                                                                        <div className='d-flex flex-column section-column'>
                                                                            <div className='mb-2'><span style={{ marginRight: '5px', fontWeight: 'bold' }}>{revenue.label}</span>
                                                                                <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip-clone" style={{ visibility: 'visible' }}>{revenue.tooltip}</Tooltip>}>
                                                                                    <i className={`fa fa-question-circle`} style={{ fontSize: "14px" }}></i>
                                                                                </OverlayTrigger>
                                                                            </div>
                                                                            <div style={{ whiteSpace: 'nowrap', marginBottom: "10px" }}>{Object.keys(this.state?.salesRevenueTableData).length > 0 ? this.state?.salesRevenueTableData?.[revenue.id] || this.state?.salesRevenueTableData?.[revenue.id] == 0 ? '$ ' + this.formatPrice(this.state.salesRevenueTableData?.[revenue.id]) : '- - -' : '- - -'}</div>
                                                                        </div>
                                                                    ))
                                                                }
                                                            </Row>
                                                        </div>
                                                    </div>
                                            }
                                    </div>
                                    </>
                                : ''}
                                    
                                {
                                    this.state.chartView ?                                            
                                        <div className='d-flex flex-column align-items-center mt-5'>  
                                            { 
                                                this.state.userChartLoading ? 
                                                    <div className="d-flex justify-content-center align-items-center" style={{ height: "54vh" }}>
                                                        <img src={`${process.env.PUBLIC_URL}/static/icons/table/option_1.gif`} alt="default" height="100px" width="100px" />
                                                    </div> : 
                                                    this.state.userChartData?.options && this.state.userChartData?.series ?                                               
                                                    <>
                                                        <div style={{ fontSize: "15px", fontWeight: "bold", marginTop: "15px" }}>User Details</div>
                                                        <Chart options={this.state.userChartData?.options} series={this.state.userChartData?.series} type="bar" className="bar-chart-resp" />
                                                    </>
                                                    : '' 
                                            }
                                            { 
                                                this.state.membershipChartLoading ? 
                                                    <div className="d-flex justify-content-center align-items-center" style={{ height: "54vh" }}>
                                                        <img src={`${process.env.PUBLIC_URL}/static/icons/table/option_1.gif`} alt="default" height="100px" width="100px" />
                                                    </div> : 
                                                    this.state.membershipChartData?.options && this.state.membershipChartData?.series ? 
                                                    <>
                                                        <div style={{ fontSize: "15px", fontWeight: "bold", marginTop: "15px" }}>Membership Details</div>
                                                        <Chart options={this.state.membershipChartData?.options} series={this.state.membershipChartData?.series} type="bar" className="bar-chart-resp2" />
                                                    </>
                                                    : '' 
                                            }
                                            { 
                                                this.state.salesChartLoading ? 
                                                    <div className="d-flex justify-content-center align-items-center" style={{ height: "54vh" }}>
                                                        <img src={`${process.env.PUBLIC_URL}/static/icons/table/option_1.gif`} alt="default" height="100px" width="100px" />
                                                    </div> : 
                                                    this.state.salesChartData?.options && this.state.salesChartData?.series ? 
                                                    <>
                                                        <div style={{ fontSize: "15px", fontWeight: "bold" }}>Sale Details</div>
                                                        <Chart options={this.state.salesChartData?.options} series={this.state.salesChartData?.series} type="bar" className="bar-chart-resp" />
                                                    </> 
                                                    : '' 
                                            }
                                            { 
                                                this.state.salesRevenueChartLoading ? 
                                                <div className="d-flex justify-content-center align-items-center" style={{ height: "54vh" }}>
                                                    <img src={`${process.env.PUBLIC_URL}/static/icons/table/option_1.gif`} alt="default" height="100px" width="100px" />
                                                </div> : 
                                                this.state.salesRevenueChartData?.options && this.state.salesRevenueChartData?.series ? 
                                                <>
                                                    <div style={{ fontSize: "15px", fontWeight: "bold" }}>Sale Revenue Details</div>
                                                    <Chart options={this.state.salesRevenueChartData?.options} series={this.state.salesRevenueChartData?.series} type="bar" className="bar-chart-resp" />
                                                </> 
                                                : '' 
                                            }
                                        </div>
                                    : ''}
                            </div>
                        </> :
                        <div>
                            <div className="d-flex justify-content-center align-items-center" style={{ height: "54vh" }}>
                                <h5>{this.state.errorMsg ? `No results found, please try your search again` : `Provide criteria to filter your search `}</h5>
                            </div>
                        </div>

                }
            </div>
        );
    }
}